import React, { useState, useEffect } from "react";
import axios from "axios";

const API = "/api";

interface ShippingRate { id: string; name: string; }
interface ReturnPolicy { id: string; name: string; }
interface ImpliedWarranty { id: string; name: string; }
interface Warranty { id: string; name: string; }
interface AdditionalService { id: string; name: string; }

interface Profile {
  id: string | null;
  shipping_rates: ShippingRate[] | null;
  return_policies: ReturnPolicy[] | null;
  implied_warranties: ImpliedWarranty[] | null;
  warranties: Warranty[] | null;
  additional_services: AdditionalService[] | null;
  responsible_producer: any | null;
  responsible_producers: any[] | null;
  responsible_persons: any[] | null;
  default_location_city: string | null;
  default_location_zip: string | null;
  default_location_province: string | null;
  default_invoice: string | null;
  safety_info_templates: {name: string; text: string}[] | null;
  synced_at: string | null;
}

interface AccountProfile {
  account_id: string;
  account_name: string;
  profile: Profile | null;
}

export default function SalesProfiles() {
  const [data, setData] = useState<AccountProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [msg, setMsg] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);
  // Stan edycji per konto
  const [edits, setEdits] = useState<{ [accountId: string]: {
    default_location_city: string;
    default_location_zip: string;
    default_location_province: string;
    default_invoice: string;
    safety_info_templates: {name: string; text: string}[];
    newSafetyText: string;
    newSafetyName: string;
  }}>({});

  const s: any = {
    btn: { border: "none", borderRadius: 6, padding: "7px 14px", fontSize: 12, cursor: "pointer", fontWeight: 500 },
    input: { border: "1px solid #d1d5db", borderRadius: 6, padding: "7px 10px", fontSize: 13, outline: "none", width: "100%", boxSizing: "border-box" as const },
    label: { fontSize: 12, color: "#6b7280", display: "block", marginBottom: 4, fontWeight: 500 },
    section: { marginBottom: 16 },
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(API + "/sales-profiles");
      setData(r.data);
      // Inicjalizuj edits dla każdego konta
      const initEdits: any = {};
      for (const ap of r.data) {
        const p = ap.profile;
        initEdits[ap.account_id] = {
          default_location_city: p?.default_location_city || "",
          default_location_zip: p?.default_location_zip || "",
          default_location_province: p?.default_location_province || "MAZOWIECKIE",
          default_invoice: p?.default_invoice || "vat",
          safety_info_templates: p?.safety_info_templates || [],
          newSafetyText: "",
          newSafetyName: "",
        };
      }
      setEdits(initEdits);
      if (r.data.length > 0 && !expanded) setExpanded(r.data[0].account_id);
    } catch (e) { setMsg("Błąd ładowania"); }
    setLoading(false);
  };

  const syncProfile = async (accountId: string) => {
    setSyncing(accountId);
    setMsg("");
    try {
      const r = await axios.post(`${API}/sales-profiles/${accountId}/sync`);
      if (r.data.errors?.length > 0) {
        setMsg("Sync OK z błędami: " + r.data.errors.join(", "));
      } else {
        setMsg("Zsynchronizowano pomyślnie");
      }
      await load();
    } catch (e) { setMsg("Błąd synchronizacji"); }
    setSyncing(null);
    setTimeout(() => setMsg(""), 5000);
  };

  const saveProfile = async (accountId: string) => {
    setSaving(accountId);
    const edit = edits[accountId];
    try {
      await axios.patch(`${API}/sales-profiles/${accountId}`, {
        default_location_city: edit.default_location_city || null,
        default_location_zip: edit.default_location_zip || null,
        default_location_province: edit.default_location_province || null,
        default_invoice: edit.default_invoice || "vat",
        safety_info_templates: edit.safety_info_templates,
      });
      setMsg("Zapisano");
      await load();
    } catch (e) { setMsg("Błąd zapisu"); }
    setSaving(null);
    setTimeout(() => setMsg(""), 3000);
  };

  const updateEdit = (accountId: string, field: string, value: any) => {
    setEdits(prev => ({ ...prev, [accountId]: { ...prev[accountId], [field]: value } }));
  };

  const addSafetyTemplate = (accountId: string) => {
    const text = edits[accountId]?.newSafetyText?.trim();
    const name = edits[accountId]?.newSafetyName?.trim();
    if (!text || !name) return;
    const newTemplate = {name, text};
    const current = edits[accountId]?.safety_info_templates || [];
    updateEdit(accountId, "safety_info_templates", [...current, newTemplate]);
    updateEdit(accountId, "newSafetyText", "");
    updateEdit(accountId, "newSafetyName", "");
  };

  const copyTemplateToAll = (template: any) => {
    data.forEach((ap: any) => {
      const current = edits[ap.account_id]?.safety_info_templates || [];
      const exists = current.some((t: any) => t.name === template.name);
      if (!exists) {
        updateEdit(ap.account_id, "safety_info_templates", [...current, template]);
      }
    });
  };

  const removeSafetyTemplate = (accountId: string, idx: number) => {
    const current = edits[accountId]?.safety_info_templates || [];
    updateEdit(accountId, "safety_info_templates", current.filter((_: any, i: number) => i !== idx));
  };

  if (loading) return <div style={{ padding: 32, textAlign: "center", color: "#9ca3af" }}>Ładowanie...</div>;

  return (
    <div style={{ padding: 16, maxWidth: 900, margin: "0 auto" }}>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 4px 0" }}>Profil sprzedażowy</h2>
        <div style={{ fontSize: 12, color: "#6b7280" }}>Ustawienia domyślne per konto Allegro — używane przy wystawianiu ofert</div>
      </div>

      {msg && <div style={{ fontSize: 12, color: "#15803d", fontWeight: 600, marginBottom: 12, padding: "6px 10px", background: "#f0fdf4", borderRadius: 6, border: "1px solid #bbf7d0" }}>{msg}</div>}

      {data.map(ap => {
        const p = ap.profile;
        const edit = edits[ap.account_id] || {};
        const isExpanded = expanded === ap.account_id;
        const isSyncing = syncing === ap.account_id;
        const isSaving = saving === ap.account_id;

        return (
          <div key={ap.account_id} style={{ border: "1px solid #e5e7eb", borderRadius: 10, marginBottom: 12, overflow: "hidden" }}>
            {/* Nagłówek konta */}
            <div onClick={() => setExpanded(isExpanded ? null : ap.account_id)}
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", background: "#f9fafb", cursor: "pointer", borderBottom: isExpanded ? "1px solid #e5e7eb" : "none" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 14, fontWeight: 700 }}>{ap.account_name}</span>
                {p?.synced_at ? (
                  <span style={{ fontSize: 11, color: "#15803d", background: "#f0fdf4", borderRadius: 4, padding: "1px 6px" }}>
                    ✓ sync {new Date(p.synced_at).toLocaleDateString("pl-PL")}
                  </span>
                ) : (
                  <span style={{ fontSize: 11, color: "#dc2626", background: "#fef2f2", borderRadius: 4, padding: "1px 6px" }}>
                    ⚠ brak sync
                  </span>
                )}
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button onClick={e => { e.stopPropagation(); syncProfile(ap.account_id); }}
                  disabled={isSyncing}
                  style={{ ...s.btn, background: "#1d4ed8", color: "#fff", fontSize: 11 }}>
                  {isSyncing ? "Synchronizuję..." : "↻ Synchronizuj z Allegro"}
                </button>
                <span style={{ color: "#9ca3af", fontSize: 16 }}>{isExpanded ? "▲" : "▼"}</span>
              </div>
            </div>

            {isExpanded && (
              <div style={{ padding: 16 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>

                  {/* LEWA KOLUMNA — dane z Allegro */}
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      Dane z Allegro
                    </div>

                    <div style={s.section}>
                      <div style={s.label}>Cenniki dostawy ({p?.shipping_rates?.length || 0})</div>
                      {p?.shipping_rates?.length ? (
                        <div style={{ fontSize: 12, color: "#374151" }}>
                          {p.shipping_rates.map((r: any) => (
                            <div key={r.id} style={{ padding: "3px 0", borderBottom: "1px solid #f3f4f6" }}>• {r.name}</div>
                          ))}
                        </div>
                      ) : <div style={{ fontSize: 12, color: "#9ca3af" }}>Brak — kliknij "Synchronizuj z Allegro"</div>}
                    </div>

                    <div style={s.section}>
                      <div style={s.label}>Warunki zwrotów ({p?.return_policies?.length || 0})</div>
                      {p?.return_policies?.length ? (
                        <div style={{ fontSize: 12, color: "#374151" }}>
                          {p.return_policies.map((r: any) => (
                            <div key={r.id} style={{ padding: "3px 0", borderBottom: "1px solid #f3f4f6" }}>• {r.name}</div>
                          ))}
                        </div>
                      ) : <div style={{ fontSize: 12, color: "#9ca3af" }}>Brak danych</div>}
                    </div>

                    <div style={s.section}>
                      <div style={s.label}>Gwarancje ({p?.warranties?.length || 0})</div>
                      {p?.warranties?.length ? (
                        <div style={{ fontSize: 12, color: "#374151" }}>
                          {p.warranties.map((r: any) => (
                            <div key={r.id} style={{ padding: "3px 0", borderBottom: "1px solid #f3f4f6" }}>• {r.name}</div>
                          ))}
                        </div>
                      ) : <div style={{ fontSize: 12, color: "#9ca3af" }}>Brak danych</div>}
                    </div>

                    <div style={s.section}>
                      <div style={s.label}>Reklamacje ({p?.implied_warranties?.length || 0})</div>
                      {p?.implied_warranties?.length ? (
                        <div style={{ fontSize: 12, color: "#374151" }}>
                          {p.implied_warranties.map((r: any) => (
                            <div key={r.id} style={{ padding: "3px 0", borderBottom: "1px solid #f3f4f6" }}>• {r.name}</div>
                          ))}
                        </div>
                      ) : <div style={{ fontSize: 12, color: "#9ca3af" }}>Brak danych</div>}
                    </div>

                    <div style={s.section}>
                      <div style={s.label}>Producenci odpowiedzialni ({p?.responsible_producers?.length || 0})</div>
                      {p?.responsible_producers?.length ? (
                        <div style={{ fontSize: 12, color: "#374151" }}>
                          {p.responsible_producers.map((r: any) => (
                            <div key={r.id} style={{ padding: "3px 0", borderBottom: "1px solid #f3f4f6" }}>• {r.name}</div>
                          ))}
                        </div>
                      ) : <div style={{ fontSize: 12, color: "#9ca3af" }}>Brak danych</div>}
                    </div>
                    <div style={s.section}>
                      <div style={s.label}>Osoby odpowiedzialne ({p?.responsible_persons?.length || 0})</div>
                      {p?.responsible_persons?.length ? (
                        <div style={{ fontSize: 12, color: "#374151" }}>
                          {p.responsible_persons.map((r: any) => (
                            <div key={r.id} style={{ padding: "3px 0", borderBottom: "1px solid #f3f4f6" }}>• {r.name}</div>
                          ))}
                        </div>
                      ) : <div style={{ fontSize: 12, color: "#9ca3af" }}>Brak danych</div>}
                    </div>
                    <div style={s.section}>
                      <div style={s.label}>Usługi dodatkowe ({p?.additional_services?.length || 0})</div>
                      {p?.additional_services?.length ? (
                        <div style={{ fontSize: 12, color: "#374151" }}>
                          {p.additional_services.map((r: any) => (
                            <div key={r.id} style={{ padding: "3px 0", borderBottom: "1px solid #f3f4f6" }}>• {r.name}</div>
                          ))}
                        </div>
                      ) : <div style={{ fontSize: 12, color: "#9ca3af" }}>Brak danych</div>}
                    </div>
                  </div>

                  {/* PRAWA KOLUMNA — ustawienia ręczne */}
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      Ustawienia ręczne
                    </div>


                    <div style={s.section}>
                      <label style={s.label}>Lokalizacja wysyłki</label>
                      <div style={{ display: "flex", gap: 8 }}>
                        <input value={edit.default_location_city || ""} onChange={e => updateEdit(ap.account_id, "default_location_city", e.target.value)}
                          placeholder="Miasto" style={{ ...s.input, flex: 2 }} />
                        <input value={edit.default_location_zip || ""} onChange={e => updateEdit(ap.account_id, "default_location_zip", e.target.value)}
                          placeholder="00-000" style={{ ...s.input, flex: 1 }} />
                      </div>
                      <select value={edit.default_location_province || "MAZOWIECKIE"} onChange={e => updateEdit(ap.account_id, "default_location_province", e.target.value)}
                        style={{ ...s.input, marginTop: 4 }}>
                        {[{v:"DOLNOSLASKIE",l:"dolnośląskie"},{v:"KUJAWSKO_POMORSKIE",l:"kujawsko-pomorskie"},{v:"LUBELSKIE",l:"lubelskie"},{v:"LUBUSKIE",l:"lubuskie"},{v:"LODZKIE",l:"łódzkie"},{v:"MALOPOLSKIE",l:"małopolskie"},{v:"MAZOWIECKIE",l:"mazowieckie"},{v:"OPOLSKIE",l:"opolskie"},{v:"PODKARPACKIE",l:"podkarpackie"},{v:"PODLASKIE",l:"podlaskie"},{v:"POMORSKIE",l:"pomorskie"},{v:"SLASKIE",l:"śląskie"},{v:"SWIETOKRZYSKIE",l:"świętokrzyskie"},{v:"WARMINSKO_MAZURSKIE",l:"warmińsko-mazurskie"},{v:"WIELKOPOLSKIE",l:"wielkopolskie"},{v:"ZACHODNIOPOMORSKIE",l:"zachodniopomorskie"}]
                          .map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                      </select>
                    </div>

                    <div style={s.section}>
                      <label style={s.label}>Domyślna faktura</label>
                      <select value={edit.default_invoice || "vat"} onChange={e => updateEdit(ap.account_id, "default_invoice", e.target.value)}
                        style={s.input}>
                        <option value="vat">Faktura VAT</option>
                        <option value="margin">Faktura marża</option>
                        <option value="none">Bez faktury</option>
                      </select>
                    </div>

                    <div style={s.section}>
                      <label style={s.label}>Szablony informacji o bezpieczeństwie</label>
                      {(edit.safety_info_templates || []).map((t: any, idx: number) => (
                        <div key={idx} style={{ display: "flex", gap: 6, alignItems: "flex-start", marginBottom: 6 }}>
                          <div style={{ flex: 1, fontSize: 12, padding: "6px 8px", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6, lineHeight: 1.5 }}>
                            <div style={{ fontWeight: 600, marginBottom: 2 }}>{t.name || t}</div>
                            {t.text && <div style={{ color: "#6b7280" }}>{t.text.substring(0, 80)}{t.text.length > 80 ? "..." : ""}</div>}
                          </div>
                          <div style={{ display: "flex", flexDirection: "column" as const, gap: 3 }}>
                            <button onClick={() => copyTemplateToAll(t)}
                              title="Skopiuj do wszystkich kont"
                              style={{ ...s.btn, background: "#eff6ff", color: "#1d4ed8", padding: "4px 8px", flexShrink: 0, fontSize: 11 }}>↓ Wszystkie</button>
                            <button onClick={() => removeSafetyTemplate(ap.account_id, idx)}
                              style={{ ...s.btn, background: "#fef2f2", color: "#dc2626", padding: "4px 8px", flexShrink: 0 }}>✕</button>
                          </div>
                        </div>
                      ))}
                      <div style={{ marginTop: 8, padding: 10, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6 }}>
                        <input value={edit.newSafetyName || ""} onChange={e => updateEdit(ap.account_id, "newSafetyName", e.target.value)}
                          placeholder="Nazwa szablonu (np. Standardowe ostrzeżenia LED)"
                          style={{ ...s.input, marginBottom: 6 }} />
                        <textarea value={edit.newSafetyText || ""} onChange={e => updateEdit(ap.account_id, "newSafetyText", e.target.value)}
                          placeholder="Treść informacji o bezpieczeństwie..."
                          rows={2} style={{ ...s.input, resize: "vertical", fontFamily: "system-ui", marginBottom: 6 }} />
                        <button onClick={() => addSafetyTemplate(ap.account_id)}
                          style={{ ...s.btn, background: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0" }}>+ Dodaj</button>
                      </div>
                    </div>

                    <button onClick={() => saveProfile(ap.account_id)} disabled={isSaving}
                      style={{ ...s.btn, background: "#1d4ed8", color: "#fff", width: "100%", padding: "10px", marginTop: 8 }}>
                      {isSaving ? "Zapisuję..." : "Zapisz ustawienia"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
